import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import { Box, IconButton, Paper, Typography } from "@mui/material";
import React, { useState } from "react";

interface NonReconSummaryLeafRow {
  action: string;
  invoiceAmount?: number | null;
  invoiceCount?: number | null;
  prRtvAmount?: number | null;
  prRtvCount?: number | null;
}

interface NonReconSummarySubRow extends NonReconSummaryLeafRow {
  subRows?: NonReconSummaryLeafRow[];
}

interface NonReconSummaryRow {
  action: string;
  invoiceAmount?: number | null;
  invoiceCount?: number | null;
  prRtvAmount?: number | null;
  prRtvCount?: number | null;
  isTopLevel?: boolean;
  isGrandTotal?: boolean;
  subRows?: NonReconSummarySubRow[];
}

// ── Static data matching the design image ─────────────────────────────────────
const STATIC_DATA: NonReconSummaryRow[] = [
  {
    action: "Resolved",
    invoiceAmount: 4214385,
    invoiceCount: 155,
    prRtvAmount: 567,
    prRtvCount: 1,
    isTopLevel: true,
  },
  {
    action: "Pending",
    invoiceAmount: 22257478,
    invoiceCount: 540,
    prRtvAmount: 12719610,
    prRtvCount: 686,
    isTopLevel: true,
    subRows: [
      {
        action: "Booking Pending - Vendor",
        invoiceAmount: 649,
        invoiceCount: 1,
        prRtvAmount: 2318,
        prRtvCount: 1,
      },
      {
        action: "Leadership Approval Required - Warehouse",
        invoiceAmount: null,
        invoiceCount: null,
        prRtvAmount: null,
        prRtvCount: null,
      },
      {
        action: "Clarification Pending - Warehouse",
        invoiceAmount: 6615995,
        invoiceCount: 309,
        prRtvAmount: 4122,
        prRtvCount: 1,
      },
      {
        action: "Approved for Booking - Finance",
        invoiceAmount: null,
        invoiceCount: null,
        prRtvAmount: null,
        prRtvCount: null,
      },
      {
        action: "Review Pending - Finance",
        invoiceAmount: 5523509,
        invoiceCount: 83,
        prRtvAmount: 2663050,
        prRtvCount: 47,
      },
      {
        action: "Require Confirmation - Procurement",
        invoiceAmount: null,
        invoiceCount: null,
        prRtvAmount: null,
        prRtvCount: null,
      },
      {
        action: "Docs Pending",
        invoiceAmount: 10117325,
        invoiceCount: 147,
        prRtvAmount: 10050120,
        prRtvCount: 637,
        subRows: [
          { action: "Vendor", invoiceAmount: 10117325, invoiceCount: 147, prRtvAmount: 9997305, prRtvCount: 636 },
          { action: "Finance", invoiceAmount: null, invoiceCount: null, prRtvAmount: null, prRtvCount: null },
          { action: "Warehouse", invoiceAmount: null, invoiceCount: null, prRtvAmount: 52815, prRtvCount: 1 },
        ],
      },
    ],
  },
  {
    action: "Grand Total",
    invoiceAmount: 26471863,
    invoiceCount: 695,
    prRtvAmount: 12720177,
    prRtvCount: 687,
    isGrandTotal: true,
  },
];

const fmt = (val: number | null | undefined): string => {
  if (val === null || val === undefined) return "";
  return val.toLocaleString("en-IN");
};

// ── Styles ────────────────────────────────────────────────────────────────────

const BORDER = "1px solid #e0e0e0";

const getActionColor = (action: string, isGrandTotal?: boolean): string => {
  if (isGrandTotal) return "#e0e0e0";
  const a = action.toLowerCase();
  if (a.includes("resolved")) return "#a5d6a7";
  if (a.includes("pending")) return "#ffcc80";
  return "#e0e0e0";
};
const CELL_FONT: React.CSSProperties = {
  fontFamily: "Inter",
  fontSize: 12,
  color: "rgba(0,0,0,0.87)",
  letterSpacing: "0.012px",
};

const cellSx = (overrides?: object) => ({
  px: 2,
  py: 0,
  borderRight: BORDER,
  display: "flex",
  alignItems: "center",
  minHeight: "51px",
  ...overrides,
});

const NonReconDetailedSummaryTable: React.FC = () => {
  const [expandedRows, setExpandedRows] = useState<Set<number>>(new Set());
  const [expandedSubRows, setExpandedSubRows] = useState<Set<string>>(new Set());

  const toggleRow = (idx: number) => {
    setExpandedRows((prev) => {
      const next = new Set(prev);
      if (next.has(idx)) next.delete(idx);
      else next.add(idx);
      return next;
    });
  };

  const toggleSubRow = (key: string) => {
    setExpandedSubRows((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  return (
    <Paper
      sx={{
        backgroundColor: "#ffffff",
        borderRadius: "8px",
        boxShadow:
          "0px 1px 3px 0px rgba(0,0,0,0.12), 0px 1px 1px 0px rgba(0,0,0,0.14), 0px 2px 1px -1px rgba(0,0,0,0.2)",
        overflow: "hidden",
        margin: "16px 0",
      }}
    >
      <Box sx={{ p: 2 }}>
        {/* <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "16px" }}>
          <Typography sx={{ fontFamily: "Inter", fontWeight: 600, fontSize: "16px", color: "rgba(0,0,0,0.87)", letterSpacing: "0.024px" }}>
            Non-Recon Disputed Summary
          </Typography>
        </Box> */}

        {/* ── Table ── */}
        <Box sx={{ border: "1px solid rgba(0,0,0,0.01)", borderRadius: "8px", overflow: "hidden" }}>
          {/* ── Top header row (Invoice / PR/RTV group spans) ── */}
          <Box sx={{ display: "flex", borderBottom: BORDER }}>
            <Box sx={{ ...cellSx({ backgroundColor: "#fafafa", flex: "0 0 260px" }) }} />
            <Box
              sx={{
                flex: 1,
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                backgroundColor: "#fafafa",
                borderRight: BORDER,
                minHeight: "51px",
              }}
            >
              <Typography sx={{ ...CELL_FONT, fontWeight: 700 }}>Invoice</Typography>
            </Box>
            <Box
              sx={{
                flex: 1,
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                backgroundColor: "#fafafa",
                minHeight: "51px",
              }}
            >
              <Typography sx={{ ...CELL_FONT, fontWeight: 700 }}>PR/RTV</Typography>
            </Box>
          </Box>

          {/* ── Sub-header row ── */}
          <Box sx={{ display: "flex", backgroundColor: "#fafafa", borderBottom: BORDER }}>
            <Box sx={{ ...cellSx({ flex: "0 0 260px", backgroundColor: "#fafafa" }) }}>
              <Typography sx={{ ...CELL_FONT, fontWeight: 700 }}>Action/Dependency</Typography>
            </Box>
            <Box sx={{ ...cellSx({ flex: 1, backgroundColor: "#fafafa" }) }}>
              <Typography sx={{ ...CELL_FONT, fontWeight: 600 }}>Invoice Amount</Typography>
            </Box>
            <Box sx={{ ...cellSx({ flex: 1, backgroundColor: "#fafafa" }) }}>
              <Typography sx={{ ...CELL_FONT, fontWeight: 600 }}># of Invoices</Typography>
            </Box>
            <Box sx={{ ...cellSx({ flex: 1, backgroundColor: "#fafafa" }) }}>
              <Typography sx={{ ...CELL_FONT, fontWeight: 600 }}>Disputed PR/RTV Amount</Typography>
            </Box>
            <Box sx={{ ...cellSx({ flex: 1, backgroundColor: "#fafafa", borderRight: "none" }) }}>
              <Typography sx={{ ...CELL_FONT, fontWeight: 600 }}># of PR/RTV Disputes</Typography>
            </Box>
          </Box>

          {/* ── Data rows ── */}
          {STATIC_DATA.map((row, rIdx) => (
            <React.Fragment key={rIdx}>
              {/* Top-level / Grand Total row */}
              <Box
                sx={{
                  display: "flex",
                  borderBottom: rIdx < STATIC_DATA.length - 1 ? BORDER : "none",
                  backgroundColor: "#ffffff",
                  cursor: row.subRows?.length ? "pointer" : "default",
                  position: "relative",
                  "&:before": {
                    content: '""',
                    position: "absolute",
                    left: 0,
                    top: 0,
                    width: "8px",
                    height: "100%",
                    backgroundColor: getActionColor(row.action, row.isGrandTotal),
                  },
                }}
                onClick={() => row.subRows?.length && toggleRow(rIdx)}
              >
                <Box sx={{ ...cellSx({ flex: "0 0 260px" }) }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                    {row.subRows?.length ? (
                      <IconButton
                        size="small"
                        sx={{ p: 0.25, color: "rgba(0,0,0,0.6)" }}
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleRow(rIdx);
                        }}
                      >
                        {expandedRows.has(rIdx) ? (
                          <KeyboardArrowDownIcon sx={{ fontSize: 16 }} />
                        ) : (
                          <KeyboardArrowRightIcon sx={{ fontSize: 16 }} />
                        )}
                      </IconButton>
                    ) : (
                      <Box sx={{ width: 24 }} />
                    )}
                    <Typography sx={{ ...CELL_FONT, fontWeight: row.isTopLevel || row.isGrandTotal ? 700 : 400 }}>
                      {row.action}
                    </Typography>
                  </Box>
                </Box>
                <Box sx={{ ...cellSx({ flex: 1 }) }}>
                  <Typography sx={{ ...CELL_FONT, fontWeight: row.isTopLevel || row.isGrandTotal ? 700 : 400 }}>
                    {fmt(row.invoiceAmount)}
                  </Typography>
                </Box>
                <Box sx={{ ...cellSx({ flex: 1 }) }}>
                  <Typography sx={{ ...CELL_FONT, fontWeight: row.isTopLevel || row.isGrandTotal ? 700 : 400 }}>
                    {fmt(row.invoiceCount)}
                  </Typography>
                </Box>
                <Box sx={{ ...cellSx({ flex: 1 }) }}>
                  <Typography sx={{ ...CELL_FONT, fontWeight: row.isTopLevel || row.isGrandTotal ? 700 : 400 }}>
                    {fmt(row.prRtvAmount)}
                  </Typography>
                </Box>
                <Box sx={{ ...cellSx({ flex: 1, borderRight: "none" }) }}>
                  <Typography sx={{ ...CELL_FONT, fontWeight: row.isTopLevel || row.isGrandTotal ? 700 : 400 }}>
                    {fmt(row.prRtvCount)}
                  </Typography>
                </Box>
              </Box>

              {/* Sub-rows */}
              {expandedRows.has(rIdx) &&
                row.subRows?.map((sub, sIdx) => {
                  const subKey = `${rIdx}-${sIdx}`;
                  const isExpandableSub = !!(sub as NonReconSummarySubRow).subRows?.length;
                  const isSubExpanded = expandedSubRows.has(subKey);
                  return (
                    <React.Fragment key={subKey}>
                      <Box
                        sx={{
                          display: "flex",
                          borderBottom: BORDER,
                          backgroundColor: "#ffffff",
                          cursor: isExpandableSub ? "pointer" : "default",
                          position: "relative",
                          "&:before": {
                            content: '""',
                            position: "absolute",
                            left: 0,
                            top: 0,
                            width: "8px",
                            height: "100%",
                            backgroundColor: getActionColor(row.action, row.isGrandTotal),
                          },
                        }}
                        onClick={() => isExpandableSub && toggleSubRow(subKey)}
                      >
                        <Box sx={{ ...cellSx({ flex: "0 0 260px", pl: "60px" }) }}>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                            {isExpandableSub ? (
                              <IconButton
                                size="small"
                                sx={{ p: 0.25, color: "rgba(0,0,0,0.6)" }}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  toggleSubRow(subKey);
                                }}
                              >
                                {isSubExpanded ? (
                                  <KeyboardArrowDownIcon sx={{ fontSize: 16 }} />
                                ) : (
                                  <KeyboardArrowRightIcon sx={{ fontSize: 16 }} />
                                )}
                              </IconButton>
                            ) : (
                              <Box sx={{ width: 24 }} />
                            )}
                            <Typography sx={{ ...CELL_FONT, fontWeight: isExpandableSub ? 600 : 400 }}>
                              {sub.action}
                            </Typography>
                          </Box>
                        </Box>
                        <Box sx={{ ...cellSx({ flex: 1 }) }}>
                          <Typography sx={{ ...CELL_FONT, fontWeight: isExpandableSub ? 600 : 400 }}>
                            {fmt(sub.invoiceAmount)}
                          </Typography>
                        </Box>
                        <Box sx={{ ...cellSx({ flex: 1 }) }}>
                          <Typography sx={{ ...CELL_FONT, fontWeight: isExpandableSub ? 600 : 400 }}>
                            {fmt(sub.invoiceCount)}
                          </Typography>
                        </Box>
                        <Box sx={{ ...cellSx({ flex: 1 }) }}>
                          <Typography sx={{ ...CELL_FONT, fontWeight: isExpandableSub ? 600 : 400 }}>
                            {fmt(sub.prRtvAmount)}
                          </Typography>
                        </Box>
                        <Box sx={{ ...cellSx({ flex: 1, borderRight: "none" }) }}>
                          <Typography sx={{ ...CELL_FONT, fontWeight: isExpandableSub ? 600 : 400 }}>
                            {fmt(sub.prRtvCount)}
                          </Typography>
                        </Box>
                      </Box>

                      {/* Sub-sub-rows (e.g. Docs Pending children) */}
                      {isSubExpanded &&
                        (sub as NonReconSummarySubRow).subRows?.map((leaf, lIdx) => (
                          <Box
                            key={`${subKey}-${lIdx}`}
                            sx={{
                              display: "flex",
                              borderBottom: BORDER,
                              backgroundColor: "#ffffff",
                              position: "relative",
                              "&:before": {
                                content: '""',
                                position: "absolute",
                                left: 0,
                                top: 0,
                                width: "8px",
                                height: "100%",
                                backgroundColor: getActionColor(row.action, row.isGrandTotal),
                              },
                            }}
                          >
                            <Box sx={{ ...cellSx({ flex: "0 0 260px", pl: "90px" }) }}>
                              <Typography sx={{ ...CELL_FONT }}>{leaf.action}</Typography>
                            </Box>
                            <Box sx={{ ...cellSx({ flex: 1 }) }}>
                              <Typography sx={{ ...CELL_FONT }}>{fmt(leaf.invoiceAmount)}</Typography>
                            </Box>
                            <Box sx={{ ...cellSx({ flex: 1 }) }}>
                              <Typography sx={{ ...CELL_FONT }}>{fmt(leaf.invoiceCount)}</Typography>
                            </Box>
                            <Box sx={{ ...cellSx({ flex: 1 }) }}>
                              <Typography sx={{ ...CELL_FONT }}>{fmt(leaf.prRtvAmount)}</Typography>
                            </Box>
                            <Box sx={{ ...cellSx({ flex: 1, borderRight: "none" }) }}>
                              <Typography sx={{ ...CELL_FONT }}>{fmt(leaf.prRtvCount)}</Typography>
                            </Box>
                          </Box>
                        ))}
                    </React.Fragment>
                  );
                })}
            </React.Fragment>
          ))}
        </Box>
      </Box>
    </Paper>
  );
};

export default NonReconDetailedSummaryTable;
